import {
  UsersThree, IdentificationBadge, ShieldCheck,
  SlidersHorizontal, Database, PlugsConnected, Wrench, Robot, User,
} from "@phosphor-icons/react";

/**
 * The Administration Console catalog. Cleaned up from 14 → 8 sections; the
 * former standalone sections (Security & Audit Logs, Environment Settings,
 * Platform Settings, Templates & Branding, Data Management, Intelligence Data
 * Ops, Agent Activity, AI Trust & Observability, Workflow & Approvals) now
 * live as tabs inside the four merged sections below.
 */
export const ADMIN_SECTIONS = [
  {
    key: "my-account", label: "My Account", icon: User, perm: "dashboard:view",
    group: "User & Access Management",
    description: "Your profile, password and notification preferences.",
  },
  {
    key: "users", label: "Users", icon: UsersThree, perm: "users:manage",
    group: "User & Access Management",
    description: "Create, edit, deactivate and assign departments to platform users.",
  },
  {
    key: "roles", label: "Roles & Permissions", icon: IdentificationBadge, perm: "users:manage",
    group: "User & Access Management",
    description: "Review the platform's roles and the granular permissions they grant.",
  },
  {
    key: "security-environment", label: "Security & Environment", icon: ShieldCheck, perm: "audit:view",
    group: "Environment & Security",
    description: "Audit trail, authentication telemetry, environment posture and production-readiness checks.",
  },
  {
    key: "platform-configuration", label: "Platform Configuration", icon: SlidersHorizontal, perm: "audit:view",
    group: "Platform Configuration",
    description: "AI provider & review policy, and branding for every export.",
  },
  {
    key: "data-intelligence", label: "Data & Intelligence", icon: Database, perm: "audit:view",
    group: "Data & Intelligence",
    description: "Data inventory and Global Intelligence directory operations (health, dedupe, sources, enrichment, batches).",
  },
  {
    key: "ai-observability", label: "AI Observability", icon: Robot, perm: "audit:view",
    group: "AI Governance",
    description: "What the AI is doing, how defensible it is, what it costs, and how work is approved.",
  },
  {
    key: "integrations", label: "Integrations", icon: PlugsConnected, perm: "audit:view",
    group: "Integrations & Health",
    description: "Manage connected apps like Stripe, LLM providers, email delivery and file storage.",
  },
  {
    key: "maintenance", label: "System Health", icon: Wrench, perm: "audit:view",
    group: "Integrations & Health",
    description: "Live service health, cache/queue status, and platform housekeeping.",
  },
];

// Aliases from the pre-cleanup section keys → the new merged section keys.
// Used by the Admin Console router to keep old bookmarks working.
export const ADMIN_SECTION_ALIASES = {
  security: "security-environment",
  environment: "security-environment",
  platform: "platform-configuration",
  templates: "platform-configuration",
  data: "data-intelligence",
  "intel-data": "data-intelligence",
  agents: "ai-observability",
  "agent-activity": "ai-observability",
  "ai-trust": "ai-observability",
  observability: "ai-observability",
  workflow: "ai-observability",
};

// Optional deep-link tab param that each alias should carry over on redirect.
export const ADMIN_ALIAS_TAB = {
  security: "audit",
  environment: "environment",
  platform: "ai",
  templates: "branding",
  data: "inventory",
  "intel-data": "ops",
  agents: "activity",
  "agent-activity": "activity",
  "ai-trust": "trust",
  observability: "cost",
  workflow: "workflow",
};

// Ordered group headings for the admin left rail. Keeping them explicit so we
// control display order regardless of section iteration order.
export const ADMIN_GROUPS = [
  "User & Access Management",
  "Environment & Security",
  "Platform Configuration",
  "Data & Intelligence",
  "AI Governance",
  "Integrations & Health",
];

export const adminSectionByKey = (key) =>
  ADMIN_SECTIONS.find((s) => s.key === key) ||
  ADMIN_SECTIONS.find((s) => s.key === ADMIN_SECTION_ALIASES[key]);

// Back-compat alias used by newer code.
export const findSectionByKey = adminSectionByKey;
